import { useState, useEffect, useCallback } from "react";
import { useNavigate, Outlet, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import PhotoPicker from "../components/PhotoPicker";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Custom hook GPS ────────────────────────────────────────
export function useGPS() {
  const [coords, setCoords] = useState(null);
  const [error, setError] = useState(null);
  const capture = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        setError("GPS no disponible en este dispositivo");
        reject(new Error("no-geo"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const c = { lat: pos.coords.latitude, lon: pos.coords.longitude, acc: pos.coords.accuracy };
          setCoords(c); setError(null); resolve(c);
        },
        (e) => { setError(e.message); reject(e); },
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 }
      );
    });
  }, []);
  return { coords, error, capture };
}

// ─── Layout chofer ─────────────────────────────────────────
export function ChoferLayout({ title, back, right, pipa, cobertura }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const pipaMostrar = pipa || (user?.pipas_asignadas || [])[0];
  return (
    <header className="chofer-topnav" data-testid="chofer-topnav">
      <div className="left">
        {back ? (
          <button className="iconbtn" onClick={() => navigate(back)} data-testid="btn-back" aria-label="Atrás">
            <i className="fa-solid fa-arrow-left" />
          </button>
        ) : (
          <div className="avatar">{(user?.nombre || "?").charAt(0)}</div>
        )}
        <div>
          <div className="title">{title || user?.nombre}</div>
          {!back && (
            <div className="sub" data-testid="header-sub">
              Pipa <strong data-testid="header-pipa-num">{pipaMostrar || "—"}</strong> · {user?.n_empleado}
              {cobertura?.es_cobertura && (
                <span className="cob-badge" data-testid="header-cobertura-badge">
                  <i className="fa-solid fa-rotate"/> Cubriendo a {cobertura.titular_nombre} · {cobertura.zona}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      <div className="right">
        {right}
        <button className="iconbtn" onClick={logout} data-testid="btn-logout" aria-label="Salir">
          <i className="fa-solid fa-right-from-bracket" />
        </button>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────
// PANTALLA 2 — MENU CHOFER (4 botones grandes)
// ─────────────────────────────────────────────────────────────
export function ChoferMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [ruta, setRuta] = useState(null);
  const [avisos, setAvisos] = useState([]);
  const [verAvisos, setVerAvisos] = useState(false);
  const [asigHoy, setAsigHoy] = useState(null);
  const [verCargaCombustible, setVerCargaCombustible] = useState(false);

  useEffect(() => {
    axios.get(`${API}/chofer/ruta-hoy`).then((r) => setRuta(r.data)).catch(() => {});
    axios.get(`${API}/chofer/notificaciones`).then((r) => setAvisos(r.data?.items || [])).catch(() => {});
    axios.get(`${API}/chofer/asignacion-hoy`).then((r) => setAsigHoy(r.data)).catch(() => {});
  }, []);

  const marcarLeida = async (id) => {
    try {
      await axios.put(`${API}/chofer/notificaciones/${id}/leida`);
      setAvisos((s) => s.filter((a) => a.id !== id));
    } catch { /* ignore */ }
  };
  const marcarTodasLeidas = async () => {
    for (const a of avisos) await marcarLeida(a.id);
    setVerAvisos(false);
  };

  const BTN = [
    { k: "ruta",     label: "MI RUTA DE HOY", sub: ruta ? `${ruta.puntos.length} parques` : "Cargando…", icon: "fa-route",            bg: "#0F766E", to: "/chofer/ruta" },
    { k: "checkin",  label: "ESTOY EN UN PUNTO", sub: "Detectar por GPS + sub-zonas",                    icon: "fa-location-crosshairs", bg: "#0284C7", to: "/chofer/intervencion" },
    { k: "registrar",label: "REGISTRAR PUNTO", sub: "Parque/camellón nuevo",                              icon: "fa-map-pin",          bg: "#047857", to: "/chofer/evento/registrar_punto" },
    { k: "garza",    label: "LLENADO GARZA",   sub: "Cargué agua",                                        icon: "fa-faucet-drip",      bg: "#1E40AF", to: "/chofer/evento/llenado_garza" },
    { k: "bomberos", label: "AYUDA BOMBEROS",  sub: "Emergencia",                                         icon: "fa-truck-medical",    bg: "#B91C1C", to: "/chofer/evento/ayuda_bomberos" },
    { k: "taller",   label: "TALLER",          sub: "Mantenimiento",                                      icon: "fa-screwdriver-wrench", bg: "#B45309", to: "/chofer/evento/taller" },
    { k: "combustible", label: "REGISTRAR CARGA", sub: "Cargué combustible",                              icon: "fa-gas-pump",         bg: "#F59E0B", to: "__modal_combustible" },
  ];

  return (
    <div className="chofer-page" data-testid="page-chofer-menu">
      <ChoferLayout pipa={ruta?.pipa_id} cobertura={ruta?.cobertura} />
      <div className="chofer-body">
        {avisos.length > 0 && (
          <button
            className="susp-banner"
            data-testid="banner-avisos-supervisor"
            style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #FCD34D", textAlign: "left", width: "100%" }}
            onClick={() => setVerAvisos(true)}
          >
            <i className="fa-solid fa-triangle-exclamation"/>
            <div>
              <strong>{avisos.length} aviso{avisos.length === 1 ? "" : "s"} del supervisor</strong>
              <div style={{ fontSize: 12, opacity: 0.85 }}>Toca para ver</div>
            </div>
            <i className="fa-solid fa-chevron-right" style={{ marginLeft: "auto" }}/>
          </button>
        )}
        {asigHoy?.es_cambio_zona && (
          <div
            className="susp-banner"
            data-testid="banner-asignacion-hoy"
            style={{ background: "#DBEAFE", color: "#1E3A8A", border: "1px solid #93C5FD", textAlign: "left" }}
          >
            <i className="fa-solid fa-map-location-dot"/>
            <div>
              <div className="susp-banner-title">
                Hoy cubres <strong>{asigHoy.zona_actual}</strong>{asigHoy.es_cambio_pipa && asigHoy.pipa_actual_id ? <> con la pipa <strong>#{asigHoy.pipa_actual_id}</strong></> : null}
              </div>
              <div className="susp-banner-sub">
                Tu zona titular es {asigHoy.zona_titular || "—"}
                {asigHoy.es_cambio_pipa && asigHoy.pipa_titular_id ? <> · tu pipa titular #{asigHoy.pipa_titular_id}</> : null}
              </div>
              {asigHoy.notas && <div className="susp-banner-comm">{asigHoy.notas}</div>}
            </div>
          </div>
        )}
        {asigHoy?.es_cambio_pipa && !asigHoy?.es_cambio_zona && (
          <div
            className="susp-banner"
            data-testid="banner-cambio-pipa"
            style={{ background: "#DBEAFE", color: "#1E3A8A", border: "1px solid #93C5FD", textAlign: "left" }}
          >
            <i className="fa-solid fa-truck-droplet"/>
            <div>
              <div className="susp-banner-title">Hoy usas la pipa <strong>#{asigHoy.pipa_actual_id}</strong> (asignación de hoy)</div>
              <div className="susp-banner-sub">Tu pipa titular es #{asigHoy.pipa_titular_id || "—"}</div>
              {asigHoy.notas && <div className="susp-banner-comm">{asigHoy.notas}</div>}
            </div>
          </div>
        )}
        {ruta?.suspension && (
          <div className="susp-banner" data-testid="banner-suspension">
            <i className="fa-solid fa-circle-exclamation"/>
            <div>
              <div className="susp-banner-title">Operación suspendida hoy por <strong>{ruta.suspension.motivo}</strong></div>
              <div className="susp-banner-sub">
                {ruta.suspension.alcance === "TODA" ? "Toda la operación" : `Zonas: ${ruta.suspension.zonas_afectadas.join(", ")}`}
                {" "}· No es necesario salir a ruta. Folio {ruta.suspension.folio}.
              </div>
              {ruta.suspension.comentario && <div className="susp-banner-comm">{ruta.suspension.comentario}</div>}
            </div>
          </div>
        )}
        <div className="chofer-hello">
          <div>
            <div className="hello-greet">Hola</div>
            <div className="hello-name">{user?.nombre}</div>
            <div className="hello-meta">
              Hoy cubres <strong data-testid="hello-zona">{ruta?.zona_id || "—"}</strong>
              {ruta?.es_cambio_zona && ruta?.zona_titular ? <span style={{ fontSize: 12, color: "#1E40AF", marginLeft: 6 }}>(titular {ruta.zona_titular})</span> : null}
              {" · "}Pipa <strong data-testid="hello-pipa">{ruta?.pipa_id || (user?.pipas_asignadas||[])[0] || "—"}</strong>
              {" · "}<span className="tag tag-water">{ruta?.tipo_agua || "—"}</span>
            </div>
            {ruta?.cobertura?.es_cobertura && (
              <div className="hello-cobertura" data-testid="hello-cobertura">
                <i className="fa-solid fa-rotate mr-1"/>
                Hoy cubres a <strong>{ruta.cobertura.titular_nombre}</strong> · {ruta.cobertura.motivo}
                {ruta.cobertura.fecha_inicio ? <> · desde {ruta.cobertura.fecha_inicio}</> : null}
              </div>
            )}
          </div>
          {ruta && (
            <div className="hello-stat">
              <div className="hello-stat-num" data-testid="hello-stat-num">{ruta.puntos.length}</div>
              <div className="hello-stat-lbl">parques</div>
              <div className="hello-stat-dia" data-testid="hello-stat-dia">{ruta.dia.toLowerCase()}</div>
            </div>
          )}
        </div>

        {/* ── PRIORIDADES (CRCs / Oficios asignados a la pipa) ── */}
        {ruta?.prioridades?.length > 0 && (
          <div className="prio-section" data-testid="prio-section">
            <div className="prio-title">
              <i className="fa-solid fa-triangle-exclamation"/> PRIORIDADES
              <span className="prio-badge" data-testid="prio-count">{ruta.prioridades.length} pendiente{ruta.prioridades.length === 1 ? "" : "s"}</span>
            </div>
            <ul className="prio-list">
              {ruta.prioridades.map((pr) => {
                const vencido = pr.prioridad === "VENCIDO" || pr.estado_temporal === "VENCIDO";
                const urgente = !vencido && pr.prioridad === "URGENTE";
                const hoy = !vencido && !urgente && pr.estado_temporal === "HOY";
                const cardClass = vencido ? "vencido" : urgente ? "urgente" : hoy ? "hoy" : "";
                return (
                  <li key={pr.id} className={`prio-card ${cardClass}`} data-testid={`prio-${pr.folio}`}>
                    <div className="prio-row1">
                      <span className="prio-origen" data-testid={`prio-origen-${pr.folio}`}>{pr.origen}</span>
                      <span className="prio-folio mono">{pr.folio}</span>
                      {vencido ? (
                        <span className="prio-tag-venc" data-testid={`prio-venc-${pr.folio}`}><i className="fa-solid fa-bell"/> VENCIDO</span>
                      ) : urgente ? (
                        <span className="prio-tag-urg" data-testid={`prio-urg-${pr.folio}`}><i className="fa-solid fa-fire"/> URGENTE</span>
                      ) : hoy ? (
                        <span className="prio-tag-hoy" data-testid={`prio-hoy-${pr.folio}`}><i className="fa-regular fa-calendar"/> HOY</span>
                      ) : pr.prioridad === "BAJA" ? (
                        <span className="prio-tag-baja">BAJA</span>
                      ) : (
                        <span className="prio-tag-alta">NORMAL</span>
                      )}
                    </div>
                    <div className="prio-direccion" data-testid={`prio-dir-${pr.folio}`}>{pr.direccion || "Sin dirección"}</div>
                    <div className="prio-meta">
                      <span><i className="fa-regular fa-clock"/> {pr.dias_pendiente === 0 ? "Asignada hoy" : `${pr.dias_pendiente} día${pr.dias_pendiente === 1 ? "" : "s"} esperando`}</span>
                      {pr.fecha_atencion && <span data-testid={`prio-fa-${pr.folio}`}><i className="fa-regular fa-calendar-check"/> {pr.fecha_atencion}</span>}
                      {pr.tipo_agua && <span className="prio-agua">{pr.tipo_agua}</span>}
                    </div>
                    <div className="prio-actions">
                      {pr.gps_lat && pr.gps_lng && (
                        <a className="prio-link-map" target="_blank" rel="noreferrer"
                           href={`https://www.google.com/maps/dir/?api=1&destination=${pr.gps_lat},${pr.gps_lng}`}
                           data-testid={`prio-ir-${pr.folio}`}>
                          <i className="fa-solid fa-location-dot"/> Ir aquí
                        </a>
                      )}
                      <button className="prio-btn-atender"
                              onClick={() => navigate(`/chofer/prioridad/${encodeURIComponent(pr.id)}`)}
                              data-testid={`prio-atender-${pr.folio}`}>
                        Atender <i className="fa-solid fa-arrow-right"/>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="chofer-actions">
          {BTN.map((b) => (
            <button
              key={b.k}
              className="action-card"
              style={{ background: b.bg }}
              onClick={() => b.to === "__modal_combustible" ? setVerCargaCombustible(true) : navigate(b.to)}
              data-testid={`btn-${b.k}`}
            >
              <i className={`fa-solid ${b.icon}`} />
              <div className="action-text">
                <div className="action-label">{b.label}</div>
                <div className="action-sub">{b.sub}</div>
              </div>
              <i className="fa-solid fa-chevron-right action-chev" />
            </button>
          ))}
        </div>
      </div>

      {verCargaCombustible && (
        <ChoferCargaCombustibleModal onClose={() => setVerCargaCombustible(false)} />
      )}

      {verAvisos && (
        <div
          onClick={() => setVerAvisos(false)}
          data-testid="modal-avisos-supervisor"
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 60, display: "grid", placeItems: "end center", padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: "white", borderRadius: 16, padding: 16, width: "100%", maxWidth: 520, maxHeight: "85vh", overflow: "auto" }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontWeight: 800, color: "#0F172A" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ color: "#D97706" }}/>{" "}
                Avisos del supervisor
              </h3>
              <button onClick={() => setVerAvisos(false)} aria-label="Cerrar" style={{ background: "transparent", border: 0, fontSize: 22, cursor: "pointer", color: "#64748B" }}>
                <i className="fa-solid fa-xmark"/>
              </button>
            </div>
            {avisos.length === 0 ? (
              <div style={{ textAlign: "center", padding: 24, color: "#94A3B8" }}>Sin avisos pendientes</div>
            ) : (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 10 }}>
                {avisos.map((a) => (
                  <li key={a.id}
                      data-testid={`aviso-${a.id}`}
                      style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 12, padding: 12 }}>
                    <div style={{ fontSize: 14, color: "#92400E", lineHeight: 1.4, whiteSpace: "pre-wrap" }}>
                      {a.mensaje}
                    </div>
                    <div style={{ fontSize: 11, color: "#78716C", marginTop: 6 }}>
                      Supervisor: {a.supervisor_nombre || "—"} · {new Date(a.fecha_notificacion).toLocaleString("es-MX")}
                    </div>
                    <button
                      onClick={() => marcarLeida(a.id)}
                      data-testid={`aviso-marcar-leida-${a.id}`}
                      style={{ marginTop: 10, background: "#0F766E", color: "white", border: 0, borderRadius: 8, padding: "8px 12px", fontWeight: 700, cursor: "pointer" }}
                    >
                      <i className="fa-solid fa-check"/> Entendido
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {avisos.length > 1 && (
              <button
                onClick={marcarTodasLeidas}
                data-testid="aviso-marcar-todas"
                style={{ marginTop: 12, width: "100%", background: "transparent", border: "1px solid #CBD5E1", borderRadius: 8, padding: 10, fontWeight: 700, cursor: "pointer", color: "#475569" }}
              >
                Marcar todos como leídos
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MODAL — REGISTRAR CARGA DE COMBUSTIBLE (chofer)
// ─────────────────────────────────────────────────────────────
function ChoferCargaCombustibleModal({ onClose }) {
  const [folioVale, setFolioVale] = useState("");
  const [folioTicket, setFolioTicket] = useState("");
  const [litros, setLitros] = useState("");
  const [odometro, setOdometro] = useState("");
  const [foto, setFoto] = useState(null);  // data URL
  const [busy, setBusy] = useState(false);

  const handleFoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fr = new FileReader();
    fr.onload = () => setFoto(fr.result);
    fr.readAsDataURL(f);
  };

  const enviar = async (e) => {
    e.preventDefault();
    if (!folioVale.trim() || !litros) {
      alert("Folio y litros son obligatorios");
      return;
    }
    if (!foto) {
      alert("La foto del odómetro es obligatoria");
      return;
    }
    setBusy(true);
    try {
      // 1) Subir foto a Cloudinary via endpoint existente
      let foto_url = "";
      try {
        const u = await axios.post(`${API}/chofer/upload-image`, { data_url: foto });
        foto_url = u.data?.url || "";
      } catch {
        // Si falla cloudinary, dejamos la foto como data url (fallback)
        foto_url = foto;
      }

      // 2) Registrar la carga (no_economico viene de la pipa del chofer)
      const ruta = await axios.get(`${API}/chofer/ruta-hoy`).catch(() => ({ data: {} }));
      const no_eco = ruta.data?.pipa_id || "";

      await axios.post(`${API}/combustible/carga`, {
        folio_vale: folioVale.trim(),
        folio_ticket_gasolinera: folioTicket.trim(),
        no_economico: no_eco,
        fecha_carga: new Date().toISOString().slice(0, 19),
        litros: parseFloat(litros),
        odometro: odometro ? parseInt(odometro, 10) : null,
        foto_odometro_url: foto_url,
        tipo_combustible: "Magna",
      });
      alert("✅ Carga registrada");
      onClose();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      data-testid="modal-chofer-combustible"
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.7)",
        zIndex: 99, display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={enviar}
        style={{
          background: "white", width: "100%", maxWidth: 520, padding: 16,
          borderRadius: "20px 20px 0 0", maxHeight: "92vh", overflowY: "auto",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-bold text-base">
            <i className="fa-solid fa-gas-pump mr-2 text-amber-600"/>Registrar carga
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar" style={{ background: "transparent", border: 0, fontSize: 22 }}>
            <i className="fa-solid fa-xmark"/>
          </button>
        </div>

        <label className="block text-xs text-slate-500 mb-1 mt-2">Folio vale *</label>
        <input
          value={folioVale}
          onChange={(e) => setFolioVale(e.target.value)}
          data-testid="inp-chofer-folio"
          required
          autoFocus
          style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", minHeight: 48, fontSize: 16 }}
        />

        <label className="block text-xs text-slate-500 mb-1 mt-3">Folio ticket gasolinera (opcional)</label>
        <input
          value={folioTicket}
          onChange={(e) => setFolioTicket(e.target.value)}
          data-testid="inp-chofer-ticket"
          style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", minHeight: 48, fontSize: 16 }}
        />

        <div className="grid grid-cols-2 gap-2 mt-3">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Litros *</label>
            <input
              type="number" step="0.001" inputMode="decimal"
              value={litros} onChange={(e) => setLitros(e.target.value)}
              data-testid="inp-chofer-litros"
              required
              style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", minHeight: 48, fontSize: 16 }}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Odómetro (opcional)</label>
            <input
              type="number" inputMode="numeric"
              value={odometro} onChange={(e) => setOdometro(e.target.value)}
              data-testid="inp-chofer-odom"
              style={{ width: "100%", border: "1px solid #cbd5e1", borderRadius: 8, padding: "10px 12px", minHeight: 48, fontSize: 16 }}
            />
          </div>
        </div>

        <label className="block text-xs text-slate-500 mb-1 mt-3">Foto odómetro *</label>
        <input
          type="file" accept="image/*" capture="environment"
          onChange={handleFoto}
          data-testid="inp-chofer-foto"
          style={{ width: "100%", minHeight: 48, fontSize: 14 }}
        />
        {foto && <img src={foto} alt="Odómetro" style={{ width: "100%", maxHeight: 200, objectFit: "contain", marginTop: 8, borderRadius: 8 }}/>}

        <button
          type="submit"
          disabled={busy}
          data-testid="btn-chofer-guardar-carga"
          style={{
            width: "100%", marginTop: 16, padding: "14px 16px",
            background: "#F59E0B", color: "white", fontWeight: 700,
            borderRadius: 10, border: 0, fontSize: 16, minHeight: 56,
          }}
        >
          <i className="fa-solid fa-floppy-disk mr-2"/>{busy ? "Guardando…" : "Registrar carga"}
        </button>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PANTALLA 3 — RUTA DEL DÍA
// ─────────────────────────────────────────────────────────────
export function ChoferRuta() {
  const navigate = useNavigate();
  const [ruta, setRuta] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/chofer/ruta-hoy`)
      .then((r) => setRuta(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  if (err) return <div className="p-6 text-red-600" data-testid="ruta-error">{err}</div>;
  if (!ruta) return <div className="p-6" data-testid="ruta-loading">Cargando ruta…</div>;

  const atendidos = ruta.puntos.filter((p) => p.estado === "ATENDIDO").length;
  const total = ruta.puntos.length;
  const fechaFmt = new Date().toLocaleDateString("es-MX", { weekday: "long", day: "2-digit", month: "long" });

  return (
    <div className="chofer-page bg-slate-50" data-testid="page-chofer-ruta">
      <header className="ruta-header">
        <button onClick={() => navigate("/chofer")} className="iconbtn-light" aria-label="Atrás" data-testid="btn-back-ruta">
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div>
          <div className="ruta-title">Pipas DMU</div>
          <div className="ruta-sub">{fechaFmt}</div>
        </div>
        <div className="ruta-count">
          <div className="num">{atendidos}<span>/{total}</span></div>
          <div className="lbl">parques</div>
        </div>
      </header>

      <div className="ruta-body">
        <div className="ruta-section-lbl">MI RUTA DE HOY</div>
        {total === 0 && (
          <div className="card-base text-center text-slate-500 py-10" data-testid="ruta-empty">
            <i className="fa-solid fa-calendar-xmark text-3xl mb-3 text-slate-300" />
            <div>No tienes ruta asignada para hoy ({ruta.dia.toLowerCase()})</div>
          </div>
        )}
        <ul className="ruta-list">
          {ruta.puntos.map((p, i) => (
            <li key={p.id || p.clave_ruta} className={`ruta-item ${p.estado!=="PENDIENTE"?"done":""}`} data-testid={`punto-${p.clave_ruta}`}>
              <div className="ruta-num">{i+1}</div>
              <div className="ruta-info">
                <div className="ruta-name">{p.nombre}</div>
                <div className="ruta-id mono">{p.clave_ruta}</div>
                {(p.ubicacion || p.colonia) && (
                  <div className="ruta-direccion" data-testid={`ruta-dir-${p.clave_ruta}`}>
                    <i className="fa-solid fa-location-dot text-slate-400"/>{" "}
                    {p.ubicacion || p.colonia}
                    {p.colonia && p.ubicacion ? ` · ${p.colonia}` : ""}
                  </div>
                )}
                <div className="ruta-pills">
                  <span className="pill-tiny pill-baja">{p.categoria || "PARQUE"}</span>
                  <span className="pill-tiny pill-zone">{p.zona_id}</span>
                  <span className="pill-tiny pill-water">TRATADA</span>
                  {p.estado === "ATENDIDO" && <span className="pill-tiny pill-ok"><i className="fa-solid fa-check"/> Atendido</span>}
                  {p.estado === "NO_LOCALIZADO" && <span className="pill-tiny pill-na">No localizado</span>}
                </div>
                <div className="ruta-actions">
                  {p.gps_lat && p.gps_lon ? (
                    <a className="link-map" target="_blank" rel="noreferrer"
                       href={`https://www.google.com/maps/dir/?api=1&destination=${p.gps_lat},${p.gps_lon}`}
                       data-testid={`btn-llegar-${p.clave_ruta}`}>
                      <i className="fa-solid fa-location-dot"/> Cómo llegar
                    </a>
                  ) : (
                    <span className="link-map link-map-off" aria-disabled="true"
                          data-testid={`btn-llegar-off-${p.clave_ruta}`}
                          title="Este parque aún no tiene coordenadas registradas">
                      <i className="fa-solid fa-location-dot"/> Sin ubicación registrada
                    </span>
                  )}
                  {p.estado === "PENDIENTE" && (
                    <button className="btn-validar"
                            onClick={() => navigate(`/chofer/validar/${encodeURIComponent(p.clave_ruta)}`)}
                            data-testid={`btn-validar-${p.clave_ruta}`}>
                      Validar punto <i className="fa-solid fa-arrow-right"/>
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>

        <button className="btn-ghost w-full mt-4" onClick={() => navigate("/chofer/resumen")} data-testid="btn-ver-resumen">
          Ver resumen del día →
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PANTALLA 4 — VALIDAR PUNTO (GPS + fotos)
// ─────────────────────────────────────────────────────────────
export function ChoferValidar() {
  const navigate = useNavigate();
  const claveRuta = decodeURIComponent(useLocation().pathname.split("/").pop());
  const [punto, setPunto] = useState(null);
  const { coords, error: gpsErr, capture } = useGPS();
  const [fotos, setFotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // Estado nuevo: tipo de validación (null = aún sin decidir) + comentarios.
  const [resultado, setResultado] = useState(null);          // "SI" | "NO" | null
  const [comentario, setComentario] = useState("");
  const [motivoSel, setMotivoSel] = useState("");            // botón rápido
  const [motivoOtro, setMotivoOtro] = useState("");          // textarea cuando Otro

  useEffect(() => {
    axios.get(`${API}/chofer/ruta-hoy`).then((r) => {
      const p = r.data.puntos.find((x) => x.clave_ruta === claveRuta);
      setPunto(p);
    });
    capture().catch(() => {});
  }, [claveRuta, capture]);

  const MOTIVOS_NO = ["Sin agua", "Acceso bloqueado", "Sin tiempo", "Punto ya atendido", "Otro"];

  const submit = async () => {
    if (resultado === null) return toast.error("Selecciona si atendiste o no el punto");
    const completado = resultado === "SI";
    if (!completado) {
      // Motivo obligatorio
      if (!motivoSel) return toast.error("Selecciona el motivo");
      if (motivoSel === "Otro" && !motivoOtro.trim()) return toast.error("Escribe el motivo");
    }
    setSubmitting(true);
    try {
      let gps = coords;
      if (!gps) { try { gps = await capture(); } catch { /* gps opcional */ } }
      const motivo_no_atencion = completado ? null : (motivoSel === "Otro" ? motivoOtro.trim() : motivoSel);
      await axios.post(`${API}/chofer/punto/${encodeURIComponent(claveRuta)}/servir`, {
        completado,
        gps_lat: gps?.lat || null,
        gps_lon: gps?.lon || null,
        fotos_count: fotos.length,
        fotos_data: fotos.map(f => f.dataURL),
        comentario: comentario.trim() || null,
        motivo_no_atencion,
      });
      navigate("/chofer/ruta");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  if (!punto) return <div className="p-6">Cargando punto…</div>;

  const distance = (() => {
    if (!coords || !punto.gps_lat || !punto.gps_lon) return null;
    const R = 6371000, toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(punto.gps_lat - coords.lat);
    const dLon = toRad(punto.gps_lon - coords.lon);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(coords.lat))*Math.cos(toRad(punto.gps_lat))*Math.sin(dLon/2)**2;
    return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)));
  })();

  return (
    <div className="chofer-page bg-slate-50" data-testid="page-validar">
      <header className="ruta-header">
        <button onClick={() => navigate("/chofer/ruta")} className="iconbtn-light" aria-label="Atrás" data-testid="btn-back-val">
          <i className="fa-solid fa-arrow-left" />
        </button>
        <div className="grow">
          <div className="ruta-title">Validar punto</div>
        </div>
      </header>

      <div className="ruta-body">
        {/* GPS banner */}
        <div className="gps-banner" data-testid="gps-banner">
          <i className="fa-solid fa-location-crosshairs"/>
          <div>
            <div className="gps-lbl">TU UBICACIÓN</div>
            {coords ? (
              <div className="gps-coords mono">{coords.lat.toFixed(4)}°N · {Math.abs(coords.lon).toFixed(4)}°W</div>
            ) : (
              <div className="gps-coords text-amber-700">{gpsErr ? `Sin GPS · ${gpsErr}` : "Capturando…"}</div>
            )}
          </div>
          <button className="btn-mini" onClick={capture} data-testid="btn-recapture">↻</button>
        </div>

        {/* Target */}
        <div className="target-card" data-testid="target-card">
          <div className="target-lbl">¿ESTÁS EN?</div>
          <div className="target-name">{punto.nombre}</div>
          <div className="target-meta mono">
            {punto.zona_id} · {punto.clave_ruta}
            {distance !== null && <span className="ml-2 text-amber-700">· {distance}m</span>}
          </div>
        </div>

        {/* Sí / No con comentario / motivos */}
        <div className="val-opciones" data-testid="val-opciones">
          <button
            className={`val-btn val-si ${resultado === "SI" ? "active" : ""}`}
            onClick={() => setResultado("SI")}
            data-testid="val-opt-si"
          >
            <i className="fa-solid fa-check"/> Sí atendí este punto
          </button>
          <button
            className={`val-btn val-no ${resultado === "NO" ? "active" : ""}`}
            onClick={() => setResultado("NO")}
            data-testid="val-opt-no"
          >
            <i className="fa-solid fa-xmark"/> No pude atender
          </button>
        </div>

        {resultado === "SI" && (
          <div className="val-block" data-testid="val-bloque-si">
            <label className="val-label">Comentario (opcional)</label>
            <textarea
              rows={2}
              value={comentario}
              onChange={(e) => setComentario(e.target.value)}
              placeholder="Ej: Se regó completo · Solo parcial"
              className="val-textarea"
              data-testid="val-comentario"
            />
          </div>
        )}

        {resultado === "NO" && (
          <div className="val-block val-block-rojo" data-testid="val-bloque-no">
            <label className="val-label">¿Por qué no se atendió? <span style={{ color: "#DC2626" }}>*</span></label>
            <div className="val-motivos">
              {MOTIVOS_NO.map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`val-motivo-pill ${motivoSel === m ? "sel" : ""}`}
                  onClick={() => setMotivoSel(m)}
                  data-testid={`val-motivo-${m.replace(/\s/g, '-')}`}
                >
                  {m}
                </button>
              ))}
            </div>
            {motivoSel === "Otro" && (
              <textarea
                rows={2}
                value={motivoOtro}
                onChange={(e) => setMotivoOtro(e.target.value)}
                placeholder="Escribe el motivo…"
                className="val-textarea"
                data-testid="val-motivo-otro-text"
                style={{ marginTop: 8 }}
              />
            )}
          </div>
        )}

        {/* Fotos (cámara o galería) */}
        <PhotoPicker fotos={fotos} setFotos={setFotos} max={5} testIdPrefix="foto-validar"/>
        <div className="text-xs text-slate-500 mt-1">Captura del antes/después del riego o estado del punto.</div>

        {/* Botón final guardar */}
        <button
          className={`val-submit ${resultado === "NO" ? "rojo" : "verde"}`}
          disabled={submitting || resultado === null}
          onClick={submit}
          data-testid="val-submit"
        >
          {submitting ? "Guardando…" : (resultado === "NO" ? "Guardar (no atendido)" : "Guardar validación")}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PANTALLA 5 — RESUMEN DEL DÍA
// ─────────────────────────────────────────────────────────────
export function ChoferResumen() {
  const navigate = useNavigate();
  const [resumen, setResumen] = useState(null);
  useEffect(() => {
    axios.get(`${API}/chofer/resumen-hoy`).then((r) => setResumen(r.data));
  }, []);
  if (!resumen) return <div className="p-6">Cargando resumen…</div>;
  const fechaFmt = new Date().toLocaleDateString("es-MX", { weekday: "long", day: "2-digit", month: "long" });
  return (
    <div className="chofer-page bg-slate-50" data-testid="page-resumen">
      <header className="ruta-header">
        <button onClick={() => navigate("/chofer/ruta")} className="iconbtn-light" data-testid="btn-back-resumen">
          <i className="fa-solid fa-arrow-left"/>
        </button>
        <div><div className="ruta-title">Resumen del día</div><div className="ruta-sub">{fechaFmt}</div></div>
      </header>

      <div className="ruta-body">
        <div className="check-big" data-testid="resumen-check"><i className="fa-solid fa-circle-check"/></div>

        <div className="resumen-list">
          <RsRow lbl="Parques atendidos"   val={resumen.atendidos} color="var(--green)"/>
          <RsRow lbl="Parques pendientes"  val={resumen.pendientes} color="var(--amber)"/>
          <RsRow lbl="No localizados"      val={resumen.no_localizados} color="var(--text-muted)"/>
          <RsRow lbl="Recargas garza/pozo" val={resumen.recargas_garza} color="var(--accent)"/>
          <RsRow lbl="Apoyos a bomberos"   val={resumen.ayuda_bomberos} color="var(--red)"/>
          <RsRow lbl="Puntos nuevos"       val={resumen.puntos_nuevos} color="var(--purple)"/>
          <RsRow lbl="Cumplimiento"        val={`${resumen.cumplimiento_pct}%`} color="var(--green)" big/>
        </div>

        <button className="btn-back-ruta" onClick={() => navigate("/chofer/ruta")} data-testid="btn-volver-ruta">
          Volver a la ruta
        </button>
      </div>
    </div>
  );
}

function RsRow({ lbl, val, color, big }) {
  return (
    <div className={`rs-row ${big?"big":""}`}>
      <span className="rs-lbl">{lbl}</span>
      <span className="rs-val" style={{ color }}>{val}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// EVENTOS rápidos (registrar punto / garza / bomberos / taller)
// ─────────────────────────────────────────────────────────────
const EVENTO_META = {
  registrar_punto: { lbl: "Registrar punto nuevo", icon: "fa-map-pin",     color: "var(--green)" , ask_nombre: true,  desc_lbl: "Ubicación / descripción" },
  llenado_garza:   { lbl: "Llenado garza / pozo",  icon: "fa-faucet-drip", color: "var(--accent)", ask_nombre: false, desc_lbl: "Garza / fuente (opcional)" },
  ayuda_bomberos:  { lbl: "Apoyo a bomberos",      icon: "fa-truck-medical", color: "var(--red)",  ask_nombre: false, desc_lbl: "Descripción de la emergencia" },
  taller:          { lbl: "Servicio de taller",    icon: "fa-screwdriver-wrench", color: "var(--amber)", ask_nombre: false, desc_lbl: "Motivo del taller" },
};

export function ChoferEvento() {
  const navigate = useNavigate();
  const tipo = useLocation().pathname.split("/").pop();
  const meta = EVENTO_META[tipo] || EVENTO_META.registrar_punto;
  const { coords, capture } = useGPS();
  const [nombre, setNombre] = useState("");
  const [desc, setDesc] = useState("");
  const [fotos, setFotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => { capture().catch(()=>{}); }, [capture]);

  const submit = async () => {
    setSubmitting(true);
    try {
      let gps = coords; if (!gps) { try { gps = await capture(); } catch { /* gps opcional */ } }
      // 12-jun-2026: clasificación automática del tipo_atencion según el botón usado.
      // Ayuda bomberos → emergencia. Resto de eventos rápidos: queda null (no es una "atención" como tal).
      const tipo_atencion = tipo === "ayuda_bomberos" ? "emergencia" : null;
      await axios.post(`${API}/chofer/evento`, {
        tipo,
        gps_lat: gps?.lat || null, gps_lon: gps?.lon || null,
        nombre: nombre || null, descripcion: desc || null,
        fotos_count: fotos.length,
        fotos_data: fotos.map(f => f.dataURL),
        tipo_atencion,
      });
      setOk(true);
      setTimeout(() => navigate("/chofer"), 1200);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="chofer-page bg-slate-50" data-testid={`page-evento-${tipo}`}>
      <header className="ruta-header">
        <button onClick={() => navigate("/chofer")} className="iconbtn-light"><i className="fa-solid fa-arrow-left"/></button>
        <div><div className="ruta-title">{meta.lbl}</div></div>
      </header>

      <div className="ruta-body space-y-4">
        <div className="evento-icon" style={{ background: meta.color }} data-testid="evento-icon"><i className={`fa-solid ${meta.icon}`}/></div>

        <div className="gps-banner">
          <i className="fa-solid fa-location-crosshairs"/>
          <div>
            <div className="gps-lbl">TU UBICACIÓN</div>
            {coords ? (
              <div className="gps-coords mono">{coords.lat.toFixed(4)}°N · {Math.abs(coords.lon).toFixed(4)}°W</div>
            ) : <div className="gps-coords text-amber-700">Capturando…</div>}
          </div>
          <button className="btn-mini" onClick={capture}>↻</button>
        </div>

        {meta.ask_nombre && (
          <div className="field">
            <label>Nombre del lugar</label>
            <input value={nombre} onChange={(e)=>setNombre(e.target.value)} placeholder="Ej: Parque Las Flores" data-testid="input-nombre-punto"/>
          </div>
        )}
        <div className="field">
          <label>{meta.desc_lbl}</label>
          <input value={desc} onChange={(e)=>setDesc(e.target.value)} placeholder="Opcional" data-testid="input-descripcion"/>
        </div>

        <PhotoPicker fotos={fotos} setFotos={setFotos} max={5} testIdPrefix="foto-evento"/>

        {ok ? (
          <div className="msg-ok" data-testid="evento-ok"><i className="fa-solid fa-circle-check"/> Registrado. Volviendo…</div>
        ) : (
          <button className="btn-primary" disabled={submitting || (meta.ask_nombre && !nombre.trim())} onClick={submit} data-testid="btn-evento-submit"
                  style={{ background: meta.color }}>
            {submitting ? "Enviando…" : "REGISTRAR"}
            <i className="fa-solid fa-check"/>
          </button>
        )}      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// PANTALLA — ATENDER PRIORIDAD (CRC / Oficio / Ciudadano)
// ─────────────────────────────────────────────────────────────
export function ChoferPrioridad() {
  const navigate = useNavigate();
  const solicitudId = decodeURIComponent(useLocation().pathname.split("/").pop());
  const [prio, setPrio] = useState(null);
  const [err, setErr] = useState(null);
  const { coords, error: gpsErr, capture } = useGPS();
  const [fotos, setFotos] = useState([]);
  const [resultado, setResultado] = useState(""); // "" | "ATENDIDO" | "NO_PUDO_ATENDER"
  const [motivoNoPudo, setMotivoNoPudo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    axios.get(`${API}/chofer/ruta-hoy`).then((r) => {
      const p = (r.data.prioridades || []).find((x) => x.id === solicitudId);
      if (p) setPrio(p);
      else setErr("Esta prioridad ya no está disponible (puede haber sido cerrada o reasignada).");
    }).catch((e) => setErr(e.response?.data?.detail || e.message));
    capture().catch(() => {});
  }, [solicitudId, capture]);

  const submit = async () => {
    if (!resultado) return;
    if (resultado === "NO_PUDO_ATENDER" && !motivoNoPudo.trim()) return;
    setSubmitting(true);
    try {
      let gps = coords;
      if (!gps) { try { gps = await capture(); } catch { /* gps opcional */ } }
      await axios.post(`${API}/chofer/prioridad/${encodeURIComponent(solicitudId)}/cerrar`, {
        resultado,
        motivo_no_pudo: resultado === "NO_PUDO_ATENDER" ? motivoNoPudo.trim() : null,
        gps_lat: gps?.lat || null,
        gps_lon: gps?.lon || null,
        fotos_count: fotos.length,
        fotos_data: fotos.map((f) => f.dataURL),
      });
      setOk(true);
      setTimeout(() => navigate("/chofer"), 1200);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (err) return (
    <div className="chofer-page bg-slate-50" data-testid="page-prio-error">
      <header className="ruta-header">
        <button onClick={() => navigate("/chofer")} className="iconbtn-light" data-testid="btn-back-prio-err"><i className="fa-solid fa-arrow-left"/></button>
        <div><div className="ruta-title">Prioridad</div></div>
      </header>
      <div className="ruta-body">
        <div className="card-base text-center text-red-700 py-8" data-testid="prio-err-msg">
          <i className="fa-solid fa-triangle-exclamation text-3xl mb-2"/><div>{err}</div>
        </div>
      </div>
    </div>
  );
  if (!prio) return <div className="p-6" data-testid="prio-loading">Cargando prioridad…</div>;

  const urgente = prio.prioridad === "URGENTE" || (prio.dias_pendiente || 0) > 2;
  const distance = (() => {
    if (!coords || !prio.gps_lat || !prio.gps_lng) return null;
    const R = 6371000, toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(prio.gps_lat - coords.lat);
    const dLon = toRad(prio.gps_lng - coords.lon);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(coords.lat)) * Math.cos(toRad(prio.gps_lat)) * Math.sin(dLon / 2) ** 2;
    return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
  })();

  return (
    <div className="chofer-page bg-slate-50" data-testid="page-prio-atender">
      <header className="ruta-header">
        <button onClick={() => navigate("/chofer")} className="iconbtn-light" aria-label="Atrás" data-testid="btn-back-prio">
          <i className="fa-solid fa-arrow-left"/>
        </button>
        <div className="grow">
          <div className="ruta-title">Atender prioridad</div>
          <div className="ruta-sub mono">{prio.folio}</div>
        </div>
      </header>

      <div className="ruta-body space-y-4">
        {/* Card de la prioridad */}
        <div className={`prio-card ${urgente ? "urgente" : ""}`} data-testid="prio-detail-card">
          <div className="prio-row1">
            <span className="prio-origen">{prio.origen}</span>
            {urgente && <span className="prio-tag-urg"><i className="fa-solid fa-fire"/> URGENTE</span>}
          </div>
          <div className="prio-direccion" data-testid="prio-detail-dir">{prio.direccion || "Sin dirección"}</div>
          <div className="prio-meta">
            <span><i className="fa-regular fa-clock"/> {prio.dias_pendiente === 0 ? "Asignada hoy" : `${prio.dias_pendiente} día${prio.dias_pendiente === 1 ? "" : "s"} esperando`}</span>
            {prio.tipo_agua && <span className="prio-agua">{prio.tipo_agua}</span>}
          </div>
          {prio.motivo && <div className="prio-motivo">{prio.motivo}</div>}
        </div>

        {/* GPS banner */}
        <div className="gps-banner" data-testid="prio-gps-banner">
          <i className="fa-solid fa-location-crosshairs"/>
          <div>
            <div className="gps-lbl">TU UBICACIÓN</div>
            {coords ? (
              <div className="gps-coords mono">{coords.lat.toFixed(4)}°N · {Math.abs(coords.lon).toFixed(4)}°W
                {distance !== null && <span className="ml-2 text-amber-700">· {distance}m al punto</span>}
              </div>
            ) : (
              <div className="gps-coords text-amber-700">{gpsErr ? `Sin GPS · ${gpsErr}` : "Capturando…"}</div>
            )}
          </div>
          <button className="btn-mini" onClick={capture} data-testid="btn-recapture-prio">↻</button>
        </div>

        {/* Resultado */}
        <div className="card-base" data-testid="prio-resultado-card">
          <div className="font-bold text-base mb-3">¿Cuál es el resultado?</div>
          <div className="space-y-2">
            <label className={`prio-radio ${resultado === "ATENDIDO" ? "sel-ok" : ""}`} data-testid="prio-opt-atendido">
              <input type="radio" name="resultado" value="ATENDIDO" checked={resultado === "ATENDIDO"} onChange={() => setResultado("ATENDIDO")} data-testid="prio-radio-atendido"/>
              <div>
                <div className="font-semibold">ATENDIDO</div>
                <div className="text-xs text-slate-500">Se realizó el servicio</div>
              </div>
            </label>
            <label className={`prio-radio ${resultado === "NO_PUDO_ATENDER" ? "sel-no" : ""}`} data-testid="prio-opt-nopudo">
              <input type="radio" name="resultado" value="NO_PUDO_ATENDER" checked={resultado === "NO_PUDO_ATENDER"} onChange={() => setResultado("NO_PUDO_ATENDER")} data-testid="prio-radio-nopudo"/>
              <div>
                <div className="font-semibold">NO SE PUDO ATENDER</div>
                <div className="text-xs text-slate-500">Vandalizado, sin acceso, dirección errónea, etc.</div>
              </div>
            </label>
          </div>

          {resultado === "NO_PUDO_ATENDER" && (
            <div className="field mt-3" data-testid="prio-motivo-wrap">
              <label>Motivo (obligatorio)</label>
              <textarea value={motivoNoPudo} onChange={(e) => setMotivoNoPudo(e.target.value)}
                        rows={3}
                        placeholder="Describe brevemente por qué no se pudo atender"
                        data-testid="prio-input-motivo"/>
            </div>
          )}
        </div>

        {/* Fotos */}
        <PhotoPicker fotos={fotos} setFotos={setFotos} max={5} testIdPrefix="foto-prio"/>
        <div className="text-xs text-slate-500 -mt-2">Foto de evidencia del servicio o de por qué no se pudo atender.</div>

        {/* Submit */}
        {ok ? (
          <div className="msg-ok" data-testid="prio-ok"><i className="fa-solid fa-circle-check"/> Guardado. Volviendo…</div>
        ) : (
          <button className="btn-primary"
                  style={{ background: resultado === "NO_PUDO_ATENDER" ? "#B91C1C" : "var(--green)" }}
                  disabled={submitting || !resultado || (resultado === "NO_PUDO_ATENDER" && !motivoNoPudo.trim())}
                  onClick={submit} data-testid="prio-btn-submit">
            {submitting ? "Guardando…" : (resultado === "NO_PUDO_ATENDER" ? "REPORTAR NO PUDO" : "GUARDAR COMO ATENDIDO")}
            <i className="fa-solid fa-check"/>
          </button>
        )}
      </div>
    </div>
  );
}
